import { Loader } from "apps/visits/packages/ui/src/components/loader";
import { useRef } from "react";
import { useParams } from "react-router";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { useGetOrder } from "../../api/get-order";
import { ThankYou } from "../../components/thank-you";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import { purchaseEvent, safeTrackEvent } from "../../tracking/events";
import { clearGaUserType, readGaUserType } from "../../utils/ga-user-type";
import { consumeVdItemListIds } from "../../utils/vd-item-list-ids";
import { configName } from "../config/config-name";

export const ThankYouPage = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { voucherDistribution } = useVouchersDistributionState();
  // Guard against duplicate purchase events if useGetOrder refetches.
  const trackedTransactionIdRef = useRef<string>();

  const { data: orderData, isLoading: isOrderLoading } = useGetOrder({
    slug: voucherDistribution?.slug || "",
    configName,
    uuid,
    config: {
      enabled: !!uuid && !!voucherDistribution,
      onSuccess: (orderData) => {
        if (trackedTransactionIdRef.current === orderData.order.uuid) return;
        trackedTransactionIdRef.current = orderData.order.uuid;

        const itemListIds = consumeVdItemListIds();

        const userType = readGaUserType();

        const totalValue = Number(orderData.order.totalGrossPrice);

        safeTrackEvent(
          () =>
            purchaseEvent(
              orderData?.order?.items.map((item) => ({
                ...item,
                productType: "blood-test-package",
                uuid: item.productUuid,
                quantity: 1,
                ...(itemListIds[item.productUuid] && { item_list_id: itemListIds[item.productUuid] }),
              })),
              {
                value: totalValue,
                transactionId: orderData.order.uuid,
              },
              {
                paymentType: totalValue === 0 ? "free" : "PayU",
                numberOfProducts: orderData.order.items.length,
                userType,
              },
            ),
          "purchase",
          "free-packages thank-you-page",
        );

        // Mirrors GAO TYP — clear once the purchase event has fired so a fresh browsing
        // session starts without a stale userType bleeding into unrelated journeys.
        clearGaUserType();
      },
    },
  });

  if (!voucherDistribution || isOrderLoading || !orderData)
    return (
      <div className="fixed inset-0 z-50 flex h-screen w-full items-center justify-center">
        <Loader />
      </div>
    );

  return (
    <IntersectionFadeIn className="mx-auto max-w-[1200px] py-10">
      <ThankYou
        vouchers={orderData.order.items}
        voucherDistribution={voucherDistribution}
        configName={configName}
        totalGrossPrice={orderData.order.totalGrossPrice}
      />
    </IntersectionFadeIn>
  );
};
